import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Clock, CheckCircle, ArrowLeft, ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { setActiveModule } from '@/utils/activeModule';

// Password strength calculator
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  const checks = {
    length: password.length >= 9,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  
  if (checks.length) score += 1;
  if (checks.lowercase) score += 1;
  if (checks.uppercase) score += 1;
  if (checks.numbers) score += 1;
  if (checks.special) score += 1;
  
  // Extra points for length
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  
  if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500', textColor: 'text-red-500', checks };
  if (score <= 4) return { score, label: 'Fair', color: 'bg-yellow-500', textColor: 'text-yellow-500', checks };
  if (score <= 5) return { score, label: 'Good', color: 'bg-blue-500', textColor: 'text-blue-500', checks };
  return { score, label: 'Strong', color: 'bg-green-500', textColor: 'text-green-500', checks };
};

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }) => {
  const strength = useMemo(() => calculatePasswordStrength(password), [password]);
  
  if (!password) return null;
  
  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${strength.color}`}
            style={{ width: `${Math.min(100, (strength.score / 7) * 100)}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${strength.textColor}`}>
          {strength.label}
        </span>
      </div>
      
      {/* Requirements checklist */}
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div className={strength.checks?.length ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.length ? '✓' : '○'} 9+ characters
        </div>
        <div className={strength.checks?.uppercase ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.uppercase ? '✓' : '○'} Uppercase
        </div>
        <div className={strength.checks?.lowercase ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.lowercase ? '✓' : '○'} Lowercase
        </div>
        <div className={strength.checks?.numbers ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.numbers ? '✓' : '○'} Number
        </div>
        <div className={strength.checks?.special ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.special ? '✓' : '○'} Special char
        </div>
      </div>
    </div>
  );
};


const Auth = () => {
  const { login, logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'register' ? 'register' : 'login';
  const resetToken = searchParams.get('reset');
  const discipline = searchParams.get('discipline'); // cnc_routing, 3d_printing, laser_cutting
  const [isLoading, setIsLoading] = useState(false);
  const [showBetaPopup, setShowBetaPopup] = useState(false);
  
  // Get discipline display name
  const getDisciplineName = (disc) => {
    const names = {
      'cnc_routing': 'CNC Router Edition',
      '3d_printing': '3D Printing Edition',
      'laser_cutting': 'Laser Edition'
    };
    return names[disc] || 'CNC-Helpdesk';
  };
  
  const getDisciplineColor = (disc) => {
    const colors = {
      'cnc_routing': 'text-[#e67e22] border-[#e67e22]',
      '3d_printing': 'text-orange-500 border-orange-500',
      'laser_cutting': 'text-red-500 border-red-500'
    };
    return colors[disc] || 'text-[#e67e22] border-[#e67e22]';
  };
  
  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetData, setResetData] = useState({ token: '', new_password: '', confirm_password: '' });

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [selectedDiscipline, setSelectedDiscipline] = useState(discipline || 'cnc_routing');
  
  // Sync selectedDiscipline with URL parameter when it changes
  useEffect(() => {
    if (discipline) {
      setSelectedDiscipline(discipline);
    }
  }, [discipline]);
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    experience_level: 'beginner'
  });

  // State for token verification
  const [tokenEmail, setTokenEmail] = useState('');
  const [tokenError, setTokenError] = useState('');

  // Check for reset token in URL - verify it and show the email
  useEffect(() => {
    const verifyToken = async () => {
      if (resetToken) {
        // Verify the token first
        try {
          const response = await axios.get(`${API}/auth/verify-reset-token/${resetToken}`);
          if (response.data.valid) {
            setResetData(prev => ({ ...prev, token: resetToken }));
            setTokenEmail(response.data.email || '');
            setTokenError('');
            setShowResetPassword(true);
          } else {
            setTokenError(response.data.message || 'Invalid reset link');
            setShowResetPassword(true); // Still show dialog to display error
          }
        } catch (error) {
          setTokenError('Failed to verify reset link. Please request a new one.');
          setShowResetPassword(true);
        }
      }
    };
    
    verifyToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Use selected discipline from dropdown (which defaults to URL param or cnc_routing)
      const activeDiscipline = selectedDiscipline;
      const loginPayload = {
        ...loginData,
        discipline: activeDiscipline || null
      };
      const response = await axios.post(`${API}/auth/login`, loginPayload);
      login(response.data.token, response.data.user);
      
      // Store the discipline as the session-locked active module
      if (activeDiscipline) {
        setActiveModule(activeDiscipline);
      }
      
      toast.success('Welcome back!');
      
      // Navigate based on selected discipline - checking if user has setup their machine
      if (activeDiscipline === '3d_printing') {
        // Check if user has a 3D printer profile
        try {
          const setupCheck = await axios.get(`${API}/printing3d/printers/has-profile`);
          if (setupCheck.data.has_profile) {
            navigate('/3d-printing');
          } else {
            navigate('/3d-printing/setup');
          }
        } catch {
          navigate('/3d-printing/setup');
        }
      } else if (activeDiscipline === 'laser_cutting') {
        // Check if user has a laser machine profile
        try {
          const setupCheck = await axios.get(`${API}/laser/machines/has-profile`);
          if (setupCheck.data.has_profile) {
            navigate('/laser');
          } else {
            navigate('/laser/setup');
          }
        } catch {
          navigate('/laser/setup');
        }
      } else if (activeDiscipline === 'admin') {
        navigate('/admin');
      } else {
        // CNC Routing - check machine profile
        try {
          const setupCheck = await axios.get(`${API}/machines/has-profile`);
          if (setupCheck.data.has_profile) {
            navigate('/dashboard');
          } else {
            navigate('/settings/machine-setup');
          }
        } catch {
          navigate('/settings/machine-setup');
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && typeof detail === 'object' && detail?.code === 'MODULE_NOT_GRANTED') {
        const modDisplay = detail.module
          ?.replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase()) || 'this module';
        toast.error(`You don't have access to ${modDisplay} yet.`, {
          action: {
            label: 'Apply for access',
            onClick: () => navigate(`/?module=${detail.module}`)
          },
          duration: 6000
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Login failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists with this email, you will receive a password reset link.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetData.new_password !== resetData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (resetData.new_password.length < 9) {
      toast.error('Password must be at least 9 characters');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetData.token,
        new_password: resetData.new_password
      });
      toast.success('Password has been reset successfully. You can now log in.');
      setShowResetPassword(false);
      setResetData({ token: '', new_password: '', confirm_password: '' });
      // Clear the reset token from URL
      navigate('/auth', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    // Validate password length
    if (registerData.password.length < 9) {
      toast.error('Password must be at least 9 characters');
      return;
    }
    setIsLoading(true);
    try {
      // Save pending registration for beta notification
      await axios.post(`${API}/auth/pending-registration`, registerData);
      // Show beta closed popup instead of logging in
      setShowBetaPopup(true);
      // Reset form
      setRegisterData({
        email: '',
        password: '',
        full_name: '',
        experience_level: 'beginner'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${IMAGES.AUTH_LOGIN})` }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex flex-col items-center justify-center gap-2 mb-4">
            <img
              src={require('@/assets/logo.png')}
              alt="CNC-Helpdesk logo"
              className="h-[14rem] sm:h-[16rem] md:h-[18rem] w-auto drop-shadow-xl"
              style={{ background: 'transparent' }}
            />
            <div className="text-sm sm:text-base font-semibold tracking-wide text-white">
              Less guesswork. Better cuts.
            </div>
          </div>
        </div>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-[#1E293B]">
            <TabsTrigger data-testid="login-tab" value="login">Login</TabsTrigger>
            <TabsTrigger data-testid="register-tab" value="register">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card className="bg-[#1E293B] border-[#334155]">
              <CardHeader>
                <CardTitle className="text-white">Welcome Back</CardTitle>
                <CardDescription className="text-zinc-400">
                  Login to access your workspace
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  {/* Discipline Selector Dropdown */}
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase text-zinc-400">
                      Login To
                    </Label>
                    <Select
                      value={selectedDiscipline}
                      onValueChange={(value) => setSelectedDiscipline(value)}
                    >
                      <SelectTrigger 
                        data-testid="discipline-select" 
                        className={`bg-[#0F172A] border-[#334155] text-white ${
                          selectedDiscipline === '3d_printing' 
                            ? 'border-orange-500 text-orange-400' 
                            : 'border-[#e67e22] text-[#e67e22]'
                        }`}
                      >
                        <SelectValue placeholder="Select Edition" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-[#334155]">
                        <SelectItem value="cnc_routing" className="text-white hover:bg-[#334155]">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#e67e22]" />
                            <span>CNC Router Edition</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="3d_printing" className="text-white hover:bg-[#334155]">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                            <span>3D Printing Edition</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="laser_cutting" className="text-white hover:bg-[#334155]">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                            <span>Laser Cutting Edition</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="admin" className="text-white hover:bg-[#334155]">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            <span>Admin</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-xs font-bold uppercase text-zinc-400">
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="bg-[#0F172A] border-[#334155] text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password" className="text-xs font-bold uppercase text-zinc-400">
                        Password
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs text-[#F59E0B] hover:text-[#F59E0B]/80 transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="bg-[#0F172A] border-[#334155] text-white"
                      required
                    />
                  </div>
                  <Button
                    data-testid="login-submit-btn"
                    type="submit"
                    className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold uppercase tracking-wide"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card className="bg-[#1E293B] border-[#334155]">
              <CardHeader>
                <CardTitle className="text-white">Create Account</CardTitle>
                <CardDescription className="text-zinc-400">Create an operator account for CNC-Helpdesk</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-xs font-bold uppercase text-zinc-400">
                      Full Name
                    </Label>
                    <Input
                      id="register-name"
                      data-testid="register-name-input"
                      type="text"
                      placeholder="John Doe"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      className="bg-[#0F172A] border-[#334155] text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-xs font-bold uppercase text-zinc-400">
                      Email
                    </Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="bg-[#0F172A] border-[#334155] text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-xs font-bold uppercase text-zinc-400">
                      Password
                    </Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      placeholder="Min 9 characters"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="bg-[#0F172A] border-[#334155] text-white"
                      required
                      minLength={9}
                    />
                    <PasswordStrengthIndicator password={registerData.password} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="experience" className="text-xs font-bold uppercase text-zinc-400">
                      Experience Level
                    </Label>
                    <Select
                      value={registerData.experience_level}
                      onValueChange={(value) => setRegisterData({ ...registerData, experience_level: value })}
                    >
                      <SelectTrigger data-testid="experience-select" className="bg-[#0F172A] border-[#334155] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-[#334155]">
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    data-testid="register-submit-btn"
                    type="submit"
                    className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold uppercase tracking-wide"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Forgot Password Dialog */}
        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent className="bg-[#1E293B] border-[#334155] max-w-md" data-testid="forgot-password-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Reset Your Password</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Enter your email address and we'll send you a link to reset your password.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleForgotPassword} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-xs font-bold uppercase text-zinc-400">
                  Email Address
                </Label>
                <Input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="bg-[#0F172A] border-[#334155] text-white"
                  required
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 border-[#334155] text-zinc-300 hover:bg-[#334155]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold"
                  disabled={isLoading}
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showResetPassword} onOpenChange={(open) => {
          setShowResetPassword(open);
          if (!open) {
            setTokenError('');
            setTokenEmail('');
          }
        }}>
          <DialogContent className="bg-[#1E293B] border-[#334155] max-w-md" data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Set New Password</DialogTitle>
              <DialogDescription className="text-zinc-400">
                {tokenError ? (
                  <span className="text-red-400">{tokenError}</span>
                ) : tokenEmail ? (
                  <>Resetting password for: <span className="text-[#F59E0B] font-semibold">{tokenEmail}</span></>
                ) : (
                  'Enter your new password below.'
                )}
              </DialogDescription>
            </DialogHeader>
            
            {tokenError ? (
              <div className="py-4 space-y-4">
                <p className="text-zinc-400 text-sm">Please request a new password reset link.</p>
                <Button
                  onClick={() => {
                    setShowResetPassword(false);
                    setShowForgotPassword(true);
                    navigate('/auth', { replace: true });
                  }}
                  className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold"
                >
                  Request New Reset Link
                </Button>
              </div>
            ) : (
            <form onSubmit={handleResetPassword} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-xs font-bold uppercase text-zinc-400">
                  New Password
                </Label>
                <Input
                  id="new-password"
                  data-testid="new-password-input"
                  type="password"
                  placeholder="Min 9 characters"
                  value={resetData.new_password}
                  onChange={(e) => setResetData({ ...resetData, new_password: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                  required
                  minLength={9}
                />
                <PasswordStrengthIndicator password={resetData.new_password} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password" className="text-xs font-bold uppercase text-zinc-400">
                  Confirm New Password
                </Label>
                <Input
                  id="confirm-new-password"
                  data-testid="confirm-new-password-input"
                  type="password"
                  placeholder="Re-enter your password"
                  value={resetData.confirm_password}
                  onChange={(e) => setResetData({ ...resetData, confirm_password: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold"
                disabled={isLoading}
                data-testid="reset-submit-btn"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Beta Closed Popup */}
        <Dialog open={showBetaPopup} onOpenChange={setShowBetaPopup}>
          <DialogContent className="bg-[#1E293B] border-[#334155] max-w-md" data-testid="beta-closed-popup">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#F59E0B]" />
                Registration Currently Closed
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-zinc-300">
                You're on the waitlist! We'll email you as soon as access opens.
              </p>
              <div className="flex items-center gap-2 p-3 bg-[#0F172A] rounded-lg border border-[#334155]">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <p className="text-sm text-zinc-300">
                  Your email has been saved — we'll reach out directly when registration opens.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowBetaPopup(false)}
              className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold"
              data-testid="beta-popup-close"
            >
              Got it
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Auth;
